import { computed, ref } from "vue";

import { PlatformType } from "@appEnum/PlatformType";
import { SiteType } from "@appEnum/SiteType";
import type { Guid } from "guid-typescript";
import { defineStore } from "pinia";
import type { IAppArgs } from "../interfaces/IAppArgs";
import type IFrontendRenderedPageMeta from "../interfaces/site/IFrontendRenderedPageMeta";
import { getCustomerPortalInteriorPagesPaths } from "../routes/cw-routes";
import { isInt } from "../utility/numberUtils";
import { fetchData } from "../utility/webServices";

export const useAppStore = defineStore("appArrrgsStore", () => {
  const appArgs = ref<IAppArgs>();
  const currentPath = ref<string>(window?.location?.pathname ?? "");

  const setMaintenanceBannerMessage = (msg: string) => {
    const a = appArgs.value;
    if (a) {
      a.MaintenanceBannerMessage = msg;
      appArgs.value = a;
    }
  };

  const setCurrentPageMeta = (pageMeta: IFrontendRenderedPageMeta) => {
    const v = appArgs.value;
    if (v) {
      currentPath.value = pageMeta.DefaultUrlRoute;
      v.PageMeta = pageMeta;
      appArgs.value = v;
    }
  };

  const witness = (
    loggingActionType: number,
    entityId1?: number,
    entityId2?: number,
    entityGuid?: Guid | string,
    entityId3?: number,
    entityObj?: unknown
  ) => {
    const uri: string[] = [loggingActionType.toString()];
    if (isInt(entityId1)) {
      uri.push(entityId1!.toString());
    }
    if (isInt(entityId2)) {
      uri.push(entityId2!.toString());
    }

    if (entityGuid !== undefined) {
      uri.push(entityGuid.toString());
    }

    if (isInt(entityId3)) {
      uri.push(entityId3!.toString());
    }

    return fetchData<boolean>(`Common/witness/${uri.join("/")}`, entityObj).catch((e) => {
      console.error("Failed to witness", e);
      return false;
    });
  };

  const isCustomerWeb = (): boolean => appArgs.value?.SiteTypeId === SiteType.CustomerWeb;

  const isInternal = (): boolean => appArgs.value?.SiteTypeId === SiteType.S1;

  const isCustomerPortal = computed(() =>
    getCustomerPortalInteriorPagesPaths().some((p) => currentPath.value.startsWith(p))
  );

  const isCustomerMobileApp = (): boolean =>
    appArgs.value?.PlatformTypeId === PlatformType.CustomerMobileAppIos ||
    appArgs.value?.PlatformTypeId === PlatformType.CustomerMobileAppAndroid;

  return {
    isCustomerWeb,
    isInternal,
    isCustomerMobileApp,
    isCustomerPortal,
    appArgs,
    setCurrentPageMeta,
    setMaintenanceBannerMessage,
    witness,
  };
});
